import React from 'react';
import { Link } from 'react-router-dom';

import {
    Button,
    GradientColumn,
    IconRightArrow,
    IconTrqkBeta,
    IconTrqkMark,
    SingerBackground,
} from '@trqk/components';

import { PATHS } from '../../constants';

const centerColumnMinWidth = 550;
const sidebarWidth = 550;

/**
 * Shows a landing page that allows the user to upload a CSV of their PRO statement in order to see
 * a preview of the Statements charts.
 *
 * The data flow goes like this:
 * 1. User selects a CSV file containing a PRO statement to upload, either with the UploadLink or
 *  the FileDropZone
 * 2. The CSV is uploaded to the S3 bucket
 * 3. Once uploaded, a URL for the object in S3 is used to send a POST request to the PRO Ingestion
 *  service
 * 4. Once the PRO ingestion completes, the user is sent to the Statements page to see data
 *  visualizations for their PRO statement
 */
export const Landing = (): JSX.Element => (
    <SingerBackground>
        <div className="w-screen h-screen">
            <div className="flex justify-between h-screen">
                <div className="flex-initial flex-col flex items-center p-8">
                    <div className="pb-4">
                        <IconTrqkMark width={50} />
                    </div>
                    <div className="pl-4">
                        <IconTrqkBeta fillColor="black" width={100} />
                    </div>
                </div>
                <div
                    className="flex-auto flex flex-col justify-between h-full"
                    style={{ minWidth: centerColumnMinWidth }}
                >
                    <div className="flex-auto pl-12 border-l border-solid border-quarternary-100 text-xs pt-12">
                        <div>modernizing</div>
                        <div>music performance</div>
                        <div>royalties</div>
                    </div>
                    <div className="flex-initial flex">
                        <div className="border-l-4 border-solid border-secondary-500 rounded-full" />
                        <div className="ml-12">
                            <h1>Performance insights</h1>
                            <h1 className="text-primary-500 pb-3">
                                in minutes for free.
                            </h1>
                        </div>
                    </div>
                    <div className="flex-auto pl-12 border-l border-solid border-quarternary-100 pt-4">
                        <p className="leading-loose">
                            Trqk digests your track catalog and PRO statement
                            <br />
                            to reveal a data-rich snapshot of your:
                        </p>
                        <ul className="leading-loose font-bold list-disc pt-3 pl-5">
                            <li>Best performing tracks</li>
                            <li>Most active markets and genres</li>
                            <li>Royalty splits across collaborators</li>
                            <li>. . . and so much more!</li>
                        </ul>
                        <div className="w-auto inline-block pt-8">
                            <Link to={PATHS.TRYOUT}>
                                <Button color="tertiary" boxShadow={true}>
                                    <span className="pr-32">Try Trqk</span>
                                    <IconRightArrow fillColor="white" />
                                </Button>
                            </Link>
                        </div>
                    </div>
                    <div className="flex-auto border-l border-solid border-quarternary-100" />
                </div>
                <div
                    className="flex-none pl-4 opacity-75"
                    style={{ width: sidebarWidth }}
                >
                    <GradientColumn color="purple">
                        <div className="pt-20 px-16 text-white flex flex-wrap justify-between font-normal">
                            <span className="flex-none px-2">About</span>
                            <span className="flex-none px-2">Team</span>
                            <span className="flex-none px-2">Jobs</span>
                            <span className="flex-none px-2">Contact</span>
                            <span className="flex-none px-2 text-white">
                                <Link to={PATHS.SIGN_IN}>Sign In</Link>
                            </span>
                        </div>
                    </GradientColumn>
                </div>
            </div>
        </div>
    </SingerBackground>
);
